import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../data/supabase.js'

export default function CadastroEmpresaPage() {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const [form, setForm] = useState({
    nomeEmpresa: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    nomeAdmin: ''
  })

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }))
    setError(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    if (!form.nomeEmpresa.trim()) return setError('Nome da empresa é obrigatório.')
    if (!form.nomeAdmin.trim()) return setError('Seu nome é obrigatório.')
    if (!form.email.trim() || !form.email.includes('@')) return setError('Email inválido.')
    if (form.senha.length < 6) return setError('Senha deve ter pelo menos 6 caracteres.')
    if (form.senha !== form.confirmarSenha) return setError('As senhas não coincidem.')

    setBusy(true)
    try {
      const email = form.email.trim().toLowerCase()
      const redirectTo = `${window.location.origin}/login`

      // 1. Tentar criar usuário no Supabase Auth
      const { data: signUpData, error: authError } = await supabase.auth.signUp({
        email,
        password: form.senha,
        options: {
          emailRedirectTo: redirectTo,
          data: { nome: form.nomeAdmin.trim(), nomeEmpresa: form.nomeEmpresa.trim() }
        }
      })

      if (authError) {
        const msg = authError.message || ''
        if (authError.status === 429 || msg.includes('rate limit') || msg.includes('Too Many'))
          throw new Error('Muitas tentativas. Aguarde alguns minutos e tente novamente.')
        if (msg.includes('User already registered'))
          throw new Error('Este email já está cadastrado. Use "Já tem conta? Entrar".')
        if (msg.includes('Password should be at least'))
          throw new Error('A senha deve ter pelo menos 6 caracteres.')
        if (msg.includes('Unable to validate email') || msg.includes('Invalid email'))
          throw new Error('Email inválido.')
        if (msg.includes('Signup is disabled'))
          throw new Error('Cadastro desativado. Entre em contato com o suporte.')
        throw new Error(msg)
      }

      // Supabase retorna identities=[] quando o email já existe mas não foi confirmado
      // Nesse caso, reenviar o email de confirmação
      const jaExiste = signUpData?.user && signUpData.user.identities?.length === 0
      if (jaExiste) {
        const { error: resendError } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { emailRedirectTo: redirectTo }
        })
        if (resendError) throw new Error('Erro ao reenviar confirmação: ' + resendError.message)
      }

      // Empresa e usuário são criados automaticamente pelo trigger
      // quando o email for confirmado (handle_new_user_signup)
      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (success) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-4">
        <div className="fixed inset-0 z-0 cm-bg" />
        <div className="fixed inset-0 z-0 cm-bg-overlay" />
        <div className="relative z-10 w-full max-w-md text-center space-y-4">
          <div className="text-5xl">📧</div>
          <h1 className="text-2xl font-bold text-white">Verifique seu email</h1>
          <p className="text-white/70 text-sm">
            Enviamos um link de confirmação para <strong className="text-white">{form.email}</strong>.
            Clique no link para ativar sua conta e acessar o sistema.
          </p>
          <p className="text-white/50 text-xs">Não recebeu? Verifique a pasta de spam.</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 w-full rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white hover:bg-accent-500"
          >
            Ir para o login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4">
      <div className="fixed inset-0 z-0 cm-bg" />
      <div className="fixed inset-0 z-0 cm-bg-overlay" />
      <div className="relative z-10 w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">Criar conta</h1>
          <p className="mt-1 text-sm text-white/60">Cadastre sua empresa no Caixa Mestre</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl cm-card p-6">
          <div>
            <label className="text-xs text-white/70">Nome da empresa *</label>
            <input
              name="nomeEmpresa"
              value={form.nomeEmpresa}
              onChange={handleChange}
              placeholder="Ex: Construtora Silva LTDA"
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm text-white outline-none"
            />
          </div>

          <div>
            <label className="text-xs text-white/70">Seu nome *</label>
            <input
              name="nomeAdmin"
              value={form.nomeAdmin}
              onChange={handleChange}
              placeholder="Ex: João Silva"
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm text-white outline-none"
            />
          </div>

          <div>
            <label className="text-xs text-white/70">Email *</label>
            <input
              name="email"
              type="email"
              autoComplete="username"
              value={form.email}
              onChange={handleChange}
              placeholder="Ex: joao@empresa.com"
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm text-white outline-none"
            />
          </div>

          <div>
            <label className="text-xs text-white/70">Senha *</label>
            <input
              name="senha"
              type="password"
              autoComplete="new-password"
              value={form.senha}
              onChange={handleChange}
              placeholder="Mínimo 6 caracteres"
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm text-white outline-none"
            />
          </div>

          <div>
            <label className="text-xs text-white/70">Confirmar senha *</label>
            <input
              name="confirmarSenha"
              type="password"
              autoComplete="new-password"
              value={form.confirmarSenha}
              onChange={handleChange}
              placeholder="Repita a senha"
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm text-white outline-none"
            />
          </div>

          {error && (
            <div className="rounded-xl bg-red-500/20 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white hover:bg-accent-500 disabled:opacity-60"
          >
            {busy ? 'Criando conta…' : 'Criar conta gratuita'}
          </button>
        </form>

        <p className="text-center text-xs text-white/50">
          Já tem conta?{' '}
          <button onClick={() => navigate('/login')} className="text-accent-400 underline">
            Entrar
          </button>
        </p>
      </div>
    </div>
  )
}
